import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';

interface SocketEvent {
  id: string;
  type: string;
  payload: unknown;
  ts: number;
  read: boolean;
}

interface SocketState {
  socket: Socket | null;
  connected: boolean;
  events: SocketEvent[];
  unreadCount: number;
  connect: (token: string) => void;
  disconnect: () => void;
  markAllRead: () => void;
  pushEvent: (event: Omit<SocketEvent, 'id' | 'ts' | 'read'>) => void;
  // Aliases used by useSocket hook
  setConnected: (connected: boolean) => void;
  addEvent: (event: { id: string; type: string; payload: unknown; receivedAt: number }) => void;
}

export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  connected: false,
  events: [],
  unreadCount: 0,

  connect: (token: string) => {
    const existing = get().socket;
    if (existing) existing.disconnect();

    const socket = io(import.meta.env.VITE_API_URL ?? 'http://localhost:3001', {
      auth: { token },
      transports: ['websocket'],
    });

    socket.on('connect', () => set({ connected: true }));
    socket.on('disconnect', () => set({ connected: false }));

    socket.on('event', (data: Omit<SocketEvent, 'id' | 'ts' | 'read'>) => {
      get().pushEvent(data);
    });

    set({ socket, connected: socket.connected });
  },

  disconnect: () => {
    get().socket?.disconnect();
    set({ socket: null, connected: false });
  },

  markAllRead: () =>
    set((s) => ({
      events: s.events.map((e) => ({ ...e, read: true })),
      unreadCount: 0,
    })),

  pushEvent: (event) =>
    set((s) => {
      const newEvent: SocketEvent = {
        ...event,
        id: Math.random().toString(36).slice(2),
        ts: Date.now(),
        read: false,
      };
      return {
        events: [newEvent, ...s.events].slice(0, 100),
        unreadCount: s.unreadCount + 1,
      };
    }),

  setConnected: (connected: boolean) => set({ connected }),

  addEvent: (event) =>
    set((s) => {
      const newEvent: SocketEvent = {
        id: event.id,
        type: event.type,
        payload: event.payload,
        ts: event.receivedAt,
        read: false,
      };
      return {
        events: [newEvent, ...s.events].slice(0, 100),
        unreadCount: s.unreadCount + 1,
      };
    }),
}));
